import { Box, Flex, Skeleton, Tabs, useMantineColorScheme } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "./PerformanceCard.module.css";

import {
  PerformanceBars,
  SortActionDirection,
  SortActionIcon,
  TextSerif,
} from "@/components/elements";
import { paths } from "@/config/paths";
import { useMarketTrends } from "@/services/market/get-trends";
import { formatNumber, toPercentFraction } from "@/utils/formatEuro";

import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type PerformanceCardTabs = "makes" | "models";

export type PerformanceCardProps = {
  defaultDirection?: SortActionDirection;
  defaultTab?: PerformanceCardTabs;
  limit?: number;
} & MantineStyleProps;

export const PerformanceCard: FC<PerformanceCardProps> = ({
  defaultDirection = "best",
  defaultTab = "models",
  limit = 5,
  ...props
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useMarketTrends();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [sortDirection, setSortDirection] = useState<"best" | "worst">(defaultDirection);

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "#a7a7a7A" : "secondary.4";

  function formaValue(value: number) {
    return formatNumber(toPercentFraction(value), {
      style: "percent",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }

  /*
    This non-DRY logic is intentionally used to prevent 'flashing' on mobile screens. 
    It may look awkward, but it works reliably for now. 

    If you plan to refactor or replace it, make sure to thoroughly test 
    the new implementation on mobile devices first.
  */
  const makesBest =
    data?.make.best.slice(0, limit).map((item, idx) => ({
      index: idx + 1,
      label: item.label,
      value: item.value,
      to: paths.app.markets.makeDetails.getHref(item.id),
    })) || [];

  const makesWorst =
    data?.make.worst.slice(0, limit).map((item, idx) => ({
      index: idx + 1,
      label: item.label,
      value: item.value,
      to: paths.app.markets.makeDetails.getHref(item.id),
    })) || [];

  const modelsBest =
    data?.model.best.slice(0, limit).map((item, idx) => ({
      index: idx + 1,
      label: item.label,
      value: item.value,
      to: paths.app.markets.car.getHref(item.id),
    })) || [];

  const modelsWorst =
    data?.model.worst.slice(0, limit).map((item, idx) => ({
      index: idx + 1,
      label: item.label,
      value: item.value,
      to: paths.app.markets.car.getHref(item.id),
    })) || [];

  const hasData = () => {
    const isMakes = activeTab === "makes";
    const isBest = sortDirection === "best";

    const dataToCheck = isMakes
      ? isBest
        ? makesBest
        : makesWorst
      : isBest
        ? modelsBest
        : modelsWorst;

    return dataToCheck.length > 0;
  };

  return (
    <Tabs defaultValue={defaultTab} value={activeTab} className={classes.tabsContainer} {...props}>
      <Flex justify="space-between" align="center">
        <Tabs.List>
          <Tabs.Tab value="makes" onClick={() => setActiveTab("makes")}>
            {t("components.PerformanceCard.Makes")}
          </Tabs.Tab>
          <Tabs.Tab value="models" onClick={() => setActiveTab("models")}>
            {t("components.PerformanceCard.Models")}
          </Tabs.Tab>
        </Tabs.List>

        {hasData() && (
          <Flex gap="md">
            <SortActionIcon
              type="best"
              active={sortDirection === "best"}
              onClick={() => setSortDirection("best")}
              ariaLabel={t("components.PerformanceCard.SortBest")}
            />
            <SortActionIcon
              type="worst"
              active={sortDirection === "worst"}
              onClick={() => setSortDirection("worst")}
              ariaLabel={t("components.PerformanceCard.SortWorst")}
            />
          </Flex>
        )}
      </Flex>

      {/* The same applies to this section! any refactor must be carefully tested on mobile! */}
      <Skeleton visible={isLoading}>
        <Flex w="100%" justify="end" py="sm">
          <TextSerif size="xs" c={textColor}>
            {t("market.table.cols.year_over_year_gain_loss")}
          </TextSerif>
        </Flex>
        <Box>
          <Tabs.Panel value="makes" display={activeTab === "makes" ? "block" : "none"}>
            <Box display={sortDirection === "best" ? "block" : "none"}>
              <PerformanceBars data={makesBest} formatValue={formaValue} align="left" />
            </Box>
            <Box display={sortDirection === "worst" ? "block" : "none"}>
              <PerformanceBars data={makesWorst} formatValue={formaValue} align="left" />
            </Box>
          </Tabs.Panel>
          <Tabs.Panel value="models" display={activeTab === "models" ? "block" : "none"}>
            <Box display={sortDirection === "best" ? "block" : "none"}>
              <PerformanceBars data={modelsBest} formatValue={formaValue} align="left" />
            </Box>
            <Box display={sortDirection === "worst" ? "block" : "none"}>
              <PerformanceBars data={modelsWorst} formatValue={formaValue} align="left" />
            </Box>
          </Tabs.Panel>
        </Box>
      </Skeleton>
    </Tabs>
  );
};
